import { useEffect, useMemo, useRef, useState } from 'react';

interface DatePickerProps {
  /** Y-m-d，空字符串表示未选 */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  invalid?: boolean;
}

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function toDateValue(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDateValue(value: string): Date | null {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function buildMonthCells(viewMonth: Date) {
  const first = startOfMonth(viewMonth);
  const startOffset = first.getDay();
  const cells: Array<{ date: Date; inMonth: boolean }> = [];
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - startOffset);

  for (let i = 0; i < 42; i++) {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + i);
    cells.push({
      date,
      inMonth: date.getMonth() === viewMonth.getMonth(),
    });
  }
  return cells;
}

/** 今天 YYYY-MM-DD */
export function todayDateValue(from = new Date()) {
  return toDateValue(from);
}

/**
 * 单日日期选择器，样式对齐 DateRangePicker / MonthPicker
 */
export default function DatePicker({
  value,
  onChange,
  placeholder = '选择日期',
  className = '',
  disabled = false,
  clearable = false,
  invalid = false,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseDateValue(value), [value]);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected || new Date()));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setViewMonth(startOfMonth(parseDateValue(value) || new Date()));
  }, [open, value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const cells = useMemo(() => buildMonthCells(viewMonth), [viewMonth]);
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthLabel = `${viewMonth.getFullYear()}年${viewMonth.getMonth() + 1}月`;
  const displayText = value || '';

  const handleDayClick = (day: Date) => {
    onChange(toDateValue(day));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const handleToday = () => {
    const v = toDateValue(today);
    onChange(v);
    setViewMonth(startOfMonth(today));
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full flex items-center gap-2 px-3 py-2 rounded-md border text-sm text-left transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
          invalid
            ? 'border-red-500 ring-1 ring-red-200 bg-white'
            : open
              ? 'border-primary-400 ring-2 ring-primary-100 bg-background-50'
              : 'border-background-200 bg-background-50 hover:border-background-300'
        }`}
      >
        <i className="ri-calendar-line text-foreground-400 shrink-0" />
        <span
          className={`flex-1 min-w-0 truncate ${
            displayText ? 'text-foreground-800' : 'text-foreground-400'
          }`}
        >
          {displayText || placeholder}
        </span>
        {clearable && value ? (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) handleClear();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (!disabled) handleClear();
              }
            }}
            className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-200 shrink-0"
            title="清除"
          >
            <i className="ri-close-circle-fill text-sm" />
          </span>
        ) : (
          <i
            className={`ri-arrow-down-s-line text-foreground-400 shrink-0 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-[120] w-[19.5rem] rounded-xl border border-background-200 bg-background-50 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-background-100">
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, -1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="上个月"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <div className="text-sm font-semibold text-foreground-900">{monthLabel}</div>
            <button
              type="button"
              onClick={() => setViewMonth((m) => addMonths(m, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors"
              title="下个月"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>

          <div className="px-3 pt-2 pb-1">
            <div className="grid grid-cols-7 mb-1">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="h-8 flex items-center justify-center text-[11px] font-medium text-foreground-400"
                >
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map(({ date, inMonth }) => {
                const dayValue = toDateValue(date);
                const isSelected = !!selected && sameDay(date, selected);
                const isToday = sameDay(date, today);

                return (
                  <button
                    key={dayValue + String(inMonth)}
                    type="button"
                    onClick={() => handleDayClick(date)}
                    className="relative h-9 flex items-center justify-center text-sm transition-colors"
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                        isSelected
                          ? 'bg-primary-500 text-background-50 font-semibold'
                          : inMonth
                            ? 'text-foreground-800 hover:bg-background-100'
                            : 'text-foreground-300 hover:bg-background-100'
                      } ${!isSelected && isToday ? 'ring-1 ring-primary-300 text-primary-600 font-medium' : ''}`}
                    >
                      {date.getDate()}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="px-3 py-2 border-t border-background-100 flex items-center justify-between gap-2">
            <p className="text-[11px] text-foreground-400 truncate">{value || '请选择日期'}</p>
            <div className="flex items-center gap-1.5 shrink-0">
              {clearable && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-2.5 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 transition-colors"
                >
                  清除
                </button>
              )}
              <button
                type="button"
                onClick={handleToday}
                className="px-2.5 py-1 rounded-md text-xs font-medium text-primary-600 hover:bg-primary-50 transition-colors"
              >
                今天
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
